import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Building2, CheckCircle2, Download, FileText, Loader2, Plus, Trash2, UserPlus,
} from "lucide-react";
import api, { PLATFORM_NAME, errMsg, money } from "@/lib/api";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** The corporate customer's own page — their travellers, their statements, no password. */
export default function CorporatePortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", cost_center: "" });
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(
    () => api.get(`/public/corporate/${token}`).then(({ data }) => setData(data))
      .catch((e) => setError(errMsg(e))),
    [token]
  );
  useEffect(() => { load(); }, [load]);

  const act = async (key, run, done) => {
    setBusy(key); setError(""); setMsg("");
    try { await run(); setMsg(done || ""); await load(); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-900 p-6">
        {error ? <p data-testid="portal-error" className="max-w-sm text-center text-sm text-red-400">{error}</p>
          : <Loader2 className="h-5 w-5 animate-spin text-gold" />}
      </div>
    );
  }
  const { account: a, company: c, statements, trips } = data;

  return (
    <div data-testid="corporate-portal" className="min-h-screen bg-ink-900 px-4 py-10 text-slate-200">
      <div className="mx-auto max-w-3xl space-y-5 rise">
        <div className="rounded-2xl border border-ink-600 bg-ink-800 p-6"
          style={{ borderTop: `3px solid ${c.branding?.primary_color || "#D4AF37"}` }}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">{c.name}</p>
              <h1 className="mt-2 flex items-center gap-2 text-2xl font-bold text-slate-50">
                <Building2 className="h-5 w-5 text-gold" /> {a.name}
              </h1>
              <p className="mt-1 text-sm text-slate-400">
                {a.terms_label} · billed monthly to {a.billing_email}
              </p>
            </div>
            <div className="text-right">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Unpaid</p>
              <p data-testid="portal-outstanding" className="font-display text-3xl font-bold text-slate-50">
                {money(a.outstanding)}
              </p>
              <p className="mt-1 text-xs text-slate-400">
                {a.trips_this_month} trip{a.trips_this_month === 1 ? "" : "s"} this month
              </p>
            </div>
          </div>
        </div>

        {error && <p data-testid="portal-action-error" className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300">{error}</p>}
        {msg && <p data-testid="portal-msg" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-300">{msg}</p>}

        <div className="rounded-2xl border border-ink-600 bg-ink-800 p-6">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
            <UserPlus className="h-3.5 w-3.5" /> Who can book on this account
          </p>
          <p className="mt-2 text-xs text-slate-500">
            Anyone listed here books without paying — their rides go on your monthly statement.
          </p>
          <div className="mt-4 space-y-2">
            {(a.travellers || []).length === 0 && (
              <p className="text-sm text-slate-500">Nobody added yet.</p>
            )}
            {(a.travellers || []).map((t) => (
              <div key={t.email} data-testid={`portal-traveller-${t.email}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-ink-600 bg-ink-900 px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-100">{t.name}</p>
                  <p className="truncate text-xs text-slate-500">
                    {t.email}{t.cost_center ? ` · ${t.cost_center}` : ""}
                  </p>
                </div>
                <button data-testid={`portal-traveller-remove-${t.email}`} title="Remove"
                  onClick={() => act(`rm-${t.email}`, () =>
                    api.delete(`/public/corporate/${token}/travellers/${encodeURIComponent(t.email)}`),
                  `${t.name} can no longer book on the account.`)}
                  className="shrink-0 text-slate-500 transition-colors hover:text-red-400">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-4">
            <Input data-testid="portal-traveller-name" placeholder="Full name" value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="h-12 border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600" />
            <Input data-testid="portal-traveller-email" placeholder="work@email.com" value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="h-12 border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600" />
            <Input data-testid="portal-traveller-cost-center"
              placeholder={a.po_required ? "Cost centre (required)" : "Cost centre (optional)"}
              value={form.cost_center}
              onChange={(e) => setForm((f) => ({ ...f, cost_center: e.target.value }))}
              className="h-12 border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600" />
            <Button data-testid="portal-traveller-add"
              disabled={busy !== "" || !form.name || !form.email
                || (a.po_required && !form.cost_center)}
              onClick={() => act("add", async () => {
                await api.post(`/public/corporate/${token}/travellers`, form);
                setForm({ name: "", email: "", phone: "", cost_center: "" });
              }, "Traveller added — they can book straight away.")}
              className="h-12 bg-gold text-ink-900 hover:bg-gold-light">
              {busy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Add</>}
            </Button>
          </div>
        </div>

        <div className="rounded-2xl border border-ink-600 bg-ink-800 p-6">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
            <FileText className="h-3.5 w-3.5" /> Statements
          </p>
          <div className="mt-4 space-y-2">
            {statements.length === 0 && (
              <p className="text-sm text-slate-500">No statements yet — the first arrives after month end.</p>
            )}
            {statements.map((s) => (
              <div key={s.id} data-testid={`portal-statement-${s.number}`}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-ink-600 bg-ink-900 p-4">
                <div>
                  <p className="text-sm font-semibold text-slate-100">
                    #{s.number} · {s.month} · {money(s.total)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {s.trips} trip{s.trips === 1 ? "" : "s"} · {s.terms_label} · due {s.due_date}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {s.status === "paid" ? (
                    <span data-testid={`portal-statement-paid-${s.number}`} className="flex items-center gap-1.5 text-sm text-emerald-300">
                      <CheckCircle2 className="h-4 w-4" /> Paid
                    </span>
                  ) : (
                    <a data-testid={`portal-statement-pay-${s.number}`} href={s.url}
                      className="flex h-10 items-center rounded-md bg-gold px-4 text-sm font-semibold text-ink-900 hover:bg-gold-light">
                      Pay {money(s.total)}
                    </a>
                  )}
                  <a data-testid={`portal-statement-pdf-${s.number}`}
                    href={`${API_BASE}/public/corporate/${token}/statements/${s.id}/pdf`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex h-10 items-center gap-1.5 rounded-md border border-ink-600 px-3 text-sm text-slate-300 hover:text-gold">
                    <Download className="h-3.5 w-3.5" /> PDF
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-ink-600 bg-ink-800 p-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Recent rides</p>
          <div className="mt-3 space-y-1.5">
            {trips.length === 0 && <p className="text-sm text-slate-500">No rides on the account yet.</p>}
            {trips.slice(0, 15).map((t) => (
              <div key={t.number} data-testid={`portal-trip-${t.number}`}
                className="flex items-center justify-between gap-3 border-b border-ink-700/60 py-2 text-sm last:border-0">
                <span className="min-w-0">
                  <span className="text-slate-200">{t.passenger}</span>
                  <span className="ml-2 text-xs text-slate-500">{t.trip_date} {t.pickup_time}</span>
                  <span className="block truncate text-xs text-slate-600">{t.pickup} → {t.dropoff}</span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="text-slate-100">{money(t.amount)}</span>
                  <span className={`ml-2 font-mono text-[10px] uppercase tracking-widest ${
                    t.billed ? "text-slate-500" : "text-amber-300"}`}>
                    {t.billed ? "billed" : "unbilled"}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-slate-500">
          Need a ride or a change? Call {c.phone || c.email} — or book at{" "}
          {c.handle ? `${window.location.origin}/book/${c.handle}` : c.name}.
        </p>
        {c.show_powered_by && (
          <p className="text-center font-mono text-[11px] text-slate-600">Powered by {PLATFORM_NAME}</p>
        )}
      </div>
    </div>
  );
}
